/**
 * Cor do avatar de uma pessoa, derivada do id.
 *
 * É **dado derivado**, não decoração: é o que faz o olho achar o Renho numa
 * lista de 22 antes de ler o nome. Por isso vive aqui e entra na tela por
 * `style={{ backgroundColor }}`, como a cor de fita (`cores.hexColor`) — e não
 * como classe do Tailwind, que faria dela dívida de cor fixa.
 *
 * ── A paleta tem SETE cores, e o número é resultado de medição ──────────────
 *
 * Duas restrições, as duas obrigatórias:
 *
 * 1. **Contraste** contra o texto branco do avatar: ≥ 4,5:1. Elimina os pesos
 *    500 e 600 nas cores quentes (amber-600 dá 3,19:1).
 * 2. **Distinção mútua**: as cores precisam ser distinguíveis ENTRE SI a 28px.
 *    Uma paleta de doze no peso 700 tinha sete pares abaixo de ΔE 20 — violet ×
 *    purple dava 7,4, que a essa escala é a mesma cor.
 *
 * Estas sete têm ΔE mínimo de 28,4 e contraste entre 5,18 e 7,10. Saíram teal,
 * indigo, purple, pink e amber: cada uma colidia com uma que ficou.
 *
 * **Não "enriqueça" a paleta de volta.** Mais cores aqui não é mais informação:
 * é a mesma informação com menos distinção. Com sete cores e ~22 funcionários há
 * repetição, e tudo bem — a cor é PISTA, não identificador; o nome está do lado.
 * O que não pode é cinco pessoas na mesma cor enquanto cinco cores sobram.
 *
 * ── A chave é o ID, não o nome ──────────────────────────────────────────────
 *
 * Chavear por nome agrupava em vez de espalhar. Medido no Modo TV em produção
 * (24/08): 15 pessoas, 6 cores em uso, e **Edemilson, Edvaldo, Erivelton,
 * Evanor e Micael todos na mesma** — o avatar era inútil justamente para o grupo
 * que mais precisava dele. A tela de TV nem hash usava: `nome.charCodeAt(0)`,
 * a primeira letra, então todo "E" caía no mesmo balde por construção.
 *
 * O UUID distribui uniforme e **não muda quando a pessoa é renomeada** — trocar
 * o apelido não troca a cor de quem já se acostumou com ela.
 */
const PALETA = [
  "#047857", // emerald-700
  "#0e7490", // cyan-700
  "#1d4ed8", // blue-700
  "#6d28d9", // violet-700
  "#a21caf", // fuchsia-700
  "#be123c", // rose-700
  "#c2410c", // orange-700
];

/** Cinza do sistema para quando não se sabe de quem é o avatar. */
export const AVATAR_SEM_NOME = "hsl(220 9% 40%)";

/** `chave` é o id do funcionário. Nunca o nome — ver o comentário acima. */
export function avatarCor(chave: string | null | undefined): string {
  if (!chave) return AVATAR_SEM_NOME;
  let h = 0;
  for (let i = 0; i < chave.length; i++) h = chave.charCodeAt(i) + ((h << 5) - h);
  return PALETA[Math.abs(h) % PALETA.length];
}
